function readDirectoryEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

function readEntryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * Recursively walks one dropped `FileSystemEntry`, so a dropped folder
 * uploads every file inside it (at any depth) rather than just its
 * top-level members. `pathPrefix` accumulates the folder names traversed so
 * far, becoming each file's path relative to the drop target.
 */
async function collectEntryFiles(
  entry: FileSystemEntry,
  pathPrefix: string,
): Promise<{ path: string; file: File }[]> {
  if (entry.isFile) {
    const file = await readEntryFile(entry as FileSystemFileEntry);
    return [{ path: `${pathPrefix}${entry.name}`, file }];
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const collected: { path: string; file: File }[] = [];
    // readEntries returns at most ~100 entries per call — keep calling until empty.
    let batch = await readDirectoryEntries(reader);
    while (batch.length > 0) {
      // A batch is read alongside itself rather than one entry after the next:
      // a folder of hundreds took hundreds of round trips before the first
      // byte could be uploaded, which is the stretch where the page has
      // nothing to show but "reading". The batches themselves stay sequential —
      // the reader is stateful and has to be drained in order — so this is
      // bounded by what one `readEntries` call returns rather than by the size
      // of the whole tree.
      const nested = await Promise.all(
        batch.map((child) =>
          collectEntryFiles(child, `${pathPrefix}${entry.name}/`),
        ),
      );
      collected.push(...nested.flat());
      batch = await readDirectoryEntries(reader);
    }
    return collected;
  }
  return [];
}

/**
 * Reads everything dropped onto the page, files and folders alike, via the
 * File and Directory Entries API (`webkitGetAsEntry`). Falls back to the
 * flat `dataTransfer.files` list for a browser/drag source that doesn't
 * expose entries at all (no folder support in that case, but plain files
 * still upload).
 */
export async function collectDroppedFiles(
  dataTransfer: DataTransfer,
): Promise<{ path: string; file: File }[]> {
  const entries = Array.from(dataTransfer.items)
    .map((item) => item.webkitGetAsEntry?.())
    .filter((entry): entry is FileSystemEntry => entry != null);

  if (entries.length === 0) {
    return Array.from(dataTransfer.files).map((file) => ({
      path: file.name,
      file,
    }));
  }

  const nested = await Promise.all(
    entries.map((entry) => collectEntryFiles(entry, '')),
  );
  return nested.flat();
}
